import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReports } from "../../../hooks/useReports";
import {
  AddSalonReportRequest,
  ReportDefinitionsResponse,
  ReportViewConfig,
  SalonReport,
} from "../../../types/reports";
import { notifyError, notifySuccess } from "../../../utils/notify";
import {
  buildCatalog,
  CatalogEntry,
  countByType,
  nextCopyName,
  ReportCatalog,
  reportDisplayName,
} from "./reportCatalog";

export interface ReportsList {
  catalog: ReportCatalog;
  reports: SalonReport[];
  loading: boolean;
  loadError: string | null;
  updateReportInList: (report: SalonReport) => void;
  /** POST a catalog entry (repeats are named "Name (2)", "(3)"…). Resolves
   *  to the new report, or null after showing an error toast. */
  addReport: (entry: CatalogEntry) => Promise<SalonReport | null>;
  /** POST a copy carrying the saved fields; `viewConfig` (a v2 report's
   *  on-screen draft) replaces the saved view. Null on failure. */
  duplicateReport: (
    report: SalonReport,
    viewConfig?: ReportViewConfig | null,
  ) => Promise<SalonReport | null>;
  /** PUT a new name / description (catalog defaults are stored as null). */
  saveReportMeta: (
    report: SalonReport,
    name: string,
    description: string,
  ) => Promise<boolean>;
  /** DELETE; resolves to the reports left, or null on failure. Copies
   *  shared with others are kept unless `deleteSharedCopies`. */
  removeReport: (
    report: SalonReport,
    options?: { deleteSharedCopies?: boolean },
  ) => Promise<SalonReport[] | null>;
  /** Record how many people now have a copy of a report (after a share or
   *  revoke in the Share modal). */
  setReportShareCount: (report: SalonReport, count: number) => void;
}

/** The fields a duplicate carries over from the saved report. */
function savedFieldsOf(
  report: SalonReport,
  entry: CatalogEntry | undefined,
  viewConfig: ReportViewConfig | null | undefined,
): Omit<AddSalonReportRequest, "report_type" | "custom_name"> {
  const view = viewConfig ?? report.view_config;
  // A new-engine report keeps its whole setup in view_config; the API
  // rejects the legacy per-column settings for it.
  if (entry?.engine === "v2") {
    return {
      custom_description: report.custom_description ?? null,
      ...(view && Object.keys(view).length > 0 ? { view_config: view } : {}),
    };
  }
  return {
    custom_description: report.custom_description ?? null,
    default_date_range: report.default_date_range,
    fixed_start_dt: report.fixed_start_dt,
    fixed_end_dt: report.fixed_end_dt,
    hidden_columns: report.hidden_columns ?? [],
    shown_columns: report.shown_columns ?? [],
    sort_config: report.sort_config ?? [],
    filter_config: report.filter_config ?? {},
  };
}

/**
 * The Reports shell's data: the catalog and the user's saved reports for a
 * location, plus every list mutation (add, duplicate, rename, remove) with
 * its success / error toast. Navigation and confirmation stay in the shell.
 */
export function useReportsList(salonId: string | undefined): ReportsList {
  const {
    listDefinitions,
    listSalonReports,
    addSalonReport,
    updateSalonReport,
    deleteSalonReport,
  } = useReports();

  const [definitions, setDefinitions] =
    useState<ReportDefinitionsResponse | null>(null);
  const [reports, setReports] = useState<SalonReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Latest list for async callers (state updates themselves are functional).
  const reportsRef = useRef(reports);
  reportsRef.current = reports;

  const catalog = useMemo(() => buildCatalog(definitions), [definitions]);

  useEffect(() => {
    if (!salonId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setLoadError(null);
        // Drop the previous location's list right away, so a failed load
        // can't leave it behind for adds and counts to act on.
        setReports([]);
        const [defsResp, reportsResp] = await Promise.all([
          listDefinitions(),
          listSalonReports(salonId),
        ]);
        if (cancelled) return;
        setDefinitions(defsResp);
        setReports(reportsResp.reports);
      } catch (e) {
        console.error("Failed to load reports:", e);
        if (!cancelled) {
          setLoadError("Could not load reports. Please refresh and try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [salonId, listDefinitions, listSalonReports]);

  const displayNames = useCallback(
    () => reportsRef.current.map((r) => reportDisplayName(r, catalog)),
    [catalog],
  );

  const insert = useCallback((report: SalonReport) => {
    setReports((prev) =>
      prev.some((r) => r.id === report.id) ? prev : [...prev, report],
    );
  }, []);

  const updateReportInList = useCallback((updated: SalonReport) => {
    setReports((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  }, []);

  const addReport = useCallback(
    async (entry: CatalogEntry): Promise<SalonReport | null> => {
      if (!salonId) return null;
      const repeat =
        (countByType(reportsRef.current).get(entry.reportType) ?? 0) > 0;
      try {
        const { report } = await addSalonReport(salonId, {
          report_type: entry.reportType,
          ...(repeat
            ? { custom_name: nextCopyName(entry.name, displayNames()) }
            : {}),
        });
        insert(report);
        notifySuccess(
          repeat
            ? "Rename it to tell the views apart."
            : "Now in your reports list.",
          {
            title: repeat
              ? `Added another ${entry.name}`
              : `Added ${entry.name}`,
          },
        );
        return report;
      } catch (e) {
        console.error("Failed to add report:", e);
        notifyError("Try again in a moment.", { title: "Couldn't add report" });
        return null;
      }
    },
    [salonId, addSalonReport, displayNames, insert],
  );

  const duplicateReport = useCallback(
    async (
      report: SalonReport,
      viewConfig?: ReportViewConfig | null,
    ): Promise<SalonReport | null> => {
      if (!salonId) return null;
      const entry = catalog.byType.get(report.report_type);
      try {
        const { report: copy } = await addSalonReport(salonId, {
          report_type: report.report_type,
          custom_name: nextCopyName(
            reportDisplayName(report, catalog),
            displayNames(),
          ),
          ...savedFieldsOf(report, entry, viewConfig),
        });
        insert(copy);
        notifySuccess(
          viewConfig
            ? "Your unsaved changes moved to the copy."
            : "Rename it to tell the views apart.",
          { title: "Report duplicated" },
        );
        return copy;
      } catch (e) {
        console.error("Failed to duplicate report:", e);
        notifyError("Try again in a moment.", {
          title: "Couldn't duplicate report",
        });
        return null;
      }
    },
    [salonId, catalog, addSalonReport, displayNames, insert],
  );

  const saveReportMeta = useCallback(
    async (
      report: SalonReport,
      name: string,
      description: string,
    ): Promise<boolean> => {
      if (!salonId) return false;
      const entry = catalog.byType.get(report.report_type);
      const trimmedName = name.trim();
      const trimmedDescription = description.trim();
      // Matching the catalog default (or blank, for the name) stores null so
      // the report keeps following the catalog copy.
      const customName =
        !trimmedName || trimmedName === entry?.name ? null : trimmedName;
      const customDescription =
        trimmedDescription === (entry?.description ?? "")
          ? null
          : trimmedDescription;
      if (
        customName === (report.custom_name ?? null) &&
        customDescription === (report.custom_description ?? null)
      ) {
        return true;
      }
      try {
        const { report: updated } = await updateSalonReport(
          salonId,
          report.id,
          { custom_name: customName, custom_description: customDescription },
        );
        updateReportInList(updated);
        return true;
      } catch (e) {
        console.error("Failed to save report name / description:", e);
        notifyError("Try again in a moment.", {
          title: "Couldn't save name & description",
        });
        return false;
      }
    },
    [salonId, catalog, updateSalonReport, updateReportInList],
  );

  const setReportShareCount = useCallback(
    (report: SalonReport, count: number) => {
      setReports((prev) =>
        prev.map((r) =>
          r.id === report.id ? { ...r, share_count: count } : r,
        ),
      );
    },
    [],
  );

  const removeReport = useCallback(
    async (
      report: SalonReport,
      { deleteSharedCopies = false }: { deleteSharedCopies?: boolean } = {},
    ): Promise<SalonReport[] | null> => {
      if (!salonId) return null;
      try {
        await deleteSalonReport(salonId, report.id, { deleteSharedCopies });
        setReports((prev) => prev.filter((r) => r.id !== report.id));
        notifySuccess(
          deleteSharedCopies
            ? "The copies you shared were removed too."
            : "Add it again anytime from Add report.",
          { title: `Removed ${reportDisplayName(report, catalog)}` },
        );
        return reportsRef.current.filter((r) => r.id !== report.id);
      } catch (e) {
        console.error("Failed to remove report:", e);
        notifyError("Try again in a moment.", {
          title: "Couldn't remove report",
        });
        return null;
      }
    },
    [salonId, catalog, deleteSalonReport],
  );

  return {
    catalog,
    reports,
    loading,
    loadError,
    updateReportInList,
    addReport,
    duplicateReport,
    saveReportMeta,
    removeReport,
    setReportShareCount,
  };
}
